import gsap, { TimelineLite } from 'gsap';

import { BaseBigWinAnimationDrawer } from './BaseBigWinAnimationDrawer';
import { bigWinMobileResources, bigWinRssNames } from '../../../core/gameConstants';
import { amountFormatter } from '../../../core/helpers/amountFormatter';
import { gameMobileConfig } from '../../../core/configs';
import { FONT_BALOO_NAME } from '../../../core/constats';
import { NumberGrowService } from '../../../services/helperService/NumberGrowService';
import { partnerService } from '../../../services/helperService/PartnerService';
import { bigWinService } from '../../../services/helperService/BigWinService';
import { getTranslation, translationKeys } from '../../../core/translation/translation';
import {gameDrawer} from "../../../game/GameDrawer";

export class BigWinAnimationMobileDrawer extends BaseBigWinAnimationDrawer {
  constructor() {
    super();
    this.container = this.createContainer('BIG_WIN');
    this.loader = this.createLoader();
    this.resources = null;
    this.loop = true;
  }

  async loadResource() {
    if (!this.resources) {
      const { res } = await super.loadResource(bigWinMobileResources, this.loader);
      this.resources = res;
    }
    return this.resources;
  }

  setBigWinCoinTexture() {
    const texture = this.createSpine(this.resources[bigWinRssNames.bigWinMobileCoins]);
    this.setPosition(texture, {
      x: gameMobileConfig.config.width / 2,
      y: gameMobileConfig.config.height,
    });
    texture.scale.set(gameMobileConfig.calculateScale(texture, 2.5));

    texture.state.setAnimation(0, 'beginning', false);
    texture.state.setAnimation(1, 'loop', true);
    this.container.addChild(texture);
  }

  setBigWinWordTexture() {
    const texture = this.createSpine(this.resources[bigWinRssNames.bigWinMobileWord]);
    this._setScale(texture);

    texture.state.setAnimation(0, 'animation', false);
    this.container.addChild(texture);
  }

  setWinAmount() {
    const amountGrow = new NumberGrowService({
      min: 0,
      max: bigWinService.amount,
      delay: 6,
      stepDelay: .05,
    });

    const rect = this.createGraphics()
      .beginFill(0x000000)
      .drawRect(
        0,
        0,
        gameMobileConfig.config.width,
        gameMobileConfig.config.height,
      ).endFill();

    rect.alpha = 0;

    const amountText = new this.PIXI.Text(amountGrow.min, {
      fill: [0xFFFFFF, 0xDFAC32],
      fontSize: 100,
      align: 'right',
      fontFamily: FONT_BALOO_NAME,
      stroke: 0xFFFFFF,
      strokeThickness: 3,
    });

    this.addSubscriber(amountGrow.subscribe((val) => {
      amountText.text = `${amountFormatter.format(val)} ${partnerService.displayCurrency}`;
    }));

    amountGrow.start();
    amountText.anchor.set(.5);
    this.setPosition(amountText, {
      x: gameMobileConfig.config.width / 2,
      y: gameMobileConfig.config.height / 2 + amountText.height / 2,
    });

    this.setupButtonMode(rect);
    this.amountGrow = amountGrow;
    this.registerClick(rect, (event) => {
      event.stopPropagation();
        this.onClick();
    });
    this.container.addChild(
      amountText,
      rect,
    );
  }

  setupPressToContinueAnimation({ texture }) {
    const duration = .5;
    return new TimelineLite()
      .to(texture, { duration, pixi: { scale: .9 } })
      .to(texture, {
        duration, pixi: { scale: 1 },
        onComplete: () => {
          if (this.loop) {
            this.setupPressToContinueAnimation({ texture });
          }
        },
      });
  }

  setPressToContinue() {
    const pressToContinue = new this.PIXI.Text(`${getTranslation(translationKeys.PRESS_ANYWHERE_TO_CONTINUE)}`, {
      fontSize: 35,
      fill: 0xf7e0b4,
      fontFamily: FONT_BALOO_NAME,
      align: 'center',
    });
    pressToContinue.anchor.set(.5);
    this.setPosition(pressToContinue, {
      x: gameMobileConfig.config.width / 2,
      y: gameMobileConfig.config.height / 2 + 180,
    });
    this.setupPressToContinueAnimation({ texture: pressToContinue });
    this.container.addChild(pressToContinue);
  }

  setMaxWinText() {
    const pressToContinue = new this.PIXI.Text(`${getTranslation(translationKeys.MAX_WIN_DESC)}`, {
      fontSize: 35,
      fill: 0xf7e0b4,
      fontFamily: FONT_BALOO_NAME,
      align: 'center',
    });
    pressToContinue.anchor.set(.5);
    this.setPosition(pressToContinue, {
      x: gameMobileConfig.config.width / 2,
      y: gameMobileConfig.config.height / 2 + 140,
    });
    this.container.addChild(pressToContinue);
  }

  setCongratulationsText() {
    const congratulations = new this.PIXI.Text(`${getTranslation(translationKeys.CONGRATULATIONS)} !!!`, {
      fontSize: 35,
      fill: 0xf7e0b4,
      fontFamily: FONT_BALOO_NAME,
      align: 'center',
    });

    const youHaveWon = new this.PIXI.Text(`${getTranslation(translationKeys.YOU_HAVE_WON)} :`, {
      fontSize: 35,
      fill: 0xf7e0b4,
      fontFamily: FONT_BALOO_NAME,
      align: 'center',
    });
    congratulations.anchor.set(.5);
    youHaveWon.anchor.set(.5);
    this.setPosition(congratulations, {
      x: gameMobileConfig.config.width / 2,
      y: gameMobileConfig.config.height / 2 - 50,
    });
    this.setPosition(youHaveWon, {
      x: gameMobileConfig.config.width / 2,
      y: gameMobileConfig.config.height / 2 - 20,
    });
    this.container.addChild(
      congratulations,
      youHaveWon,
    );
  }

  setupSubscriber() {
    this.addSubscriber(bigWinService.subscribeState(state => {
      if (state) {
        this.startBigWin();
      } else {
        this.loop = false;
        this.container.children = [];
      }
    }, true));
  }

  _setupHiddenAnimation(texture, index, duration) {
    new TimelineLite().to(texture, { delay: (index + 1) * duration, duration: duration / 2, alpha: 0 });
  }

  _setScale(texture) {
    this.setPosition(texture, {
      x: gameMobileConfig.config.width / 2,
      y: gameMobileConfig.config.height,
    });

    texture.scale.set(gameMobileConfig.calculateScale(texture, .85));
    const mistake = gameMobileConfig.config.height / 2 - texture.height;
    this.setPosition(texture, {
      x: gameMobileConfig.config.width / 2,
      y: gameMobileConfig.config.height - mistake,
    });
  }

  setEpicWinTexture({ index = 1, duration = 1 } = {}) {
    const texture = this.createSpine(this.resources[bigWinRssNames.mobileEpicWin]);
    texture.alpha = 0;
    this._setScale(texture);
    setTimeout(() => {
      texture.alpha = 1;
      texture.state.setAnimation(0, 'animation', false);
    }, index * duration * 1000);
    this._setupHiddenAnimation(texture, index, duration);
    this.container.addChild(texture);
  }

  setMaxWinTexture({ index = 3, duration = 1 } = {}) {
    const texture = this.createSpine(this.resources[bigWinRssNames.mobileMaxWin]);
    texture.alpha = 0;
    this._setScale(texture);
    setTimeout(() => {
      texture.alpha = 1;
      texture.state.setAnimation(0, 'animation', false);
    }, index * duration * 1000);
    this.container.addChild(texture);
  }

  setMegaWinTexture({ index = 2, duration = 1 } = {}) {
    const texture = this.createSpine(this.resources[bigWinRssNames.mobileMegaWin]);
    texture.alpha = 0;
    this._setScale(texture);
    setTimeout(() => {
      texture.alpha = 1;
      texture.state.setAnimation(0, 'animation', false);
    }, index * duration * 1000);
    this._setupHiddenAnimation(texture, index, duration);
    this.container.addChild(texture);
  }

  setSuperWinTexture({ index = 0, duration = 1 } = {}) {
    const texture = this.createSpine(this.resources[bigWinRssNames.mobileSuperWin]);
    texture.alpha = 0;
    this._setScale(texture);
    setTimeout(() => {
      texture.alpha = 1;
      texture.state.setAnimation(0, 'animation', false);
    }, index * duration * 1000);

    this._setupHiddenAnimation(texture, index, duration);
    this.container.addChild(texture);
  }

  setMaxWinCoins() {
    const texture = this.createSprite(this.resources[bigWinRssNames.mobileCoins]);
    const texture1 = this.createSprite(this.resources[bigWinRssNames.mobileCoins2]);
    texture.anchor.set(.5, 1);
    texture1.anchor.set(.5, 1);
    texture.scale.set(gameMobileConfig.config.width / texture.width);
    this.setPosition(texture, {
      x: gameMobileConfig.config.width / 2,
      y: gameMobileConfig.config.height + texture.height,
    });

    texture1.scale.set(gameMobileConfig.config.width / texture1.width);
    this.setPosition(texture1, {
      x: gameMobileConfig.config.width / 2,
      y: gameMobileConfig.config.height + texture1.height,
    });

    gsap.to(texture, {
      duration: 4,
      y: gameMobileConfig.config.height,
    });
    gsap.to(texture1, {
      delay: 1,
      duration: 4,
      y: gameMobileConfig.config.height,
    });
    this.container.addChild(
      texture,
      texture1,
    );
  }

  startBigWin() {
    this.container.children = [];
    this.loop = true;
    this.setBigWinCoinTexture();
    const duration = 1;

    if (bigWinService.isMaxWin) {
      this.setSuperWinTexture({ index: 0, duration });
      this.setEpicWinTexture({ index: 1, duration });
      this.setMegaWinTexture({ index: 2, duration });
      this.setMaxWinTexture({ index: 3, duration });
      this.setMaxWinText();
      this.setMaxWinCoins();
    } else {
      this.setBigWinWordTexture();
    }
    this.setWinAmount();
    this.setPressToContinue();
    this.setCongratulationsText();
  }

  async onLoad() {
    return this.loadResource();
  }

  async executeBigWinAnimation({ root }) {
    await this.loadResource();
    this.setupSubscriber();
    root.addChild(this.container);
  }
}
